import { useState } from 'react';
import dot from 'dot-object';
import pathOr from 'ramda.pathor';

import { sortByProp } from 'utils';

import {
  APPLICATION_CONVENTIONAL,
  APPLICATION_PRIMARY_AFFORDABLE,
  APPLICATION_SECONDARY_AFFORDABLE,
  APPLICATION_PRIMARY_RECERT,
  APPLICATION_SECONDARY_RECERT,
  APPLICATION_CONVENTIONAL_WA,
  APPLICATION_PRIMARY_AFFORDABLE_WA,
  APPLICATION_SECONDARY_AFFORDABLE_WA,
  APPLICATION_PRIMARY_AFFORDABLE_WITH_GLOBAL_DISABILITY,
  APPLICATION_SECONDARY_AFFORDABLE_WITH_GLOBAL_DISABILITY,
} from 'utils/constants';
import { HOUSEHOLD_TYPE_AFFORDABLE } from 'utils/constants/household';
import {
  ALT_ANNUAL_INCOME_NAME,
  ALT_CASH_VALUE_NAME,
  ALT_FEES_TO_CONVERT_CASH_NAME,
  ALT_INTEREST_RATE_NAME,
  ALT_MARKET_VALUE_NAME,
} from 'components/Organisms/FormIncomeAndAssets/common';

import {
  calculateIncome,
  payFrequencyOptionsMap,
  ALT_INCOME_FORM_FIELD_NAMES,
} from 'components/Organisms/FormIncomeAndAssets/configIncome';

import { ALT_ASSET_FORM_FIELD_NAMES } from 'components/Organisms/FormIncomeAndAssets/configAssets';

/**
 * Parses options returned from the backend into a format of:
 *
 * `[{ label: <name>, value: <id> }, ...]`
 *
 * Can take a parserOptions object with the following properties:
 *
 * sorted {boolean}: sets sorting to true/false
 * labelKey {string}: changes the property being used to set the `label` value
 * valueKey {string}: changes the property being used to set the `value` value
 * additionalKeys {Array<string>}: adds new key/value pairs returned array items
 *
 * @param {Array<options>} responseValues Array of items from the backend
 * @param {Object} parserOptions Optional object to change parser behavior
 */
export const parseOptions = (responseValues, parserOptions = {}) => {
  const defaultParserOptions = {
    sorted: true,
    labelKey: 'name',
    valueKey: 'id',
    additionalKeys: [],
  };
  const { sorted, labelKey, valueKey, additionalKeys } = {
    ...defaultParserOptions,
    ...parserOptions,
  };
  let opts = !Array.isArray(responseValues)
    ? []
    : responseValues.reduce((prev, opt) => {
        const newItem = {
          label:
            labelKey instanceof Array
              ? pathOr('', labelKey, opt)
              : opt[labelKey],
          value:
            valueKey instanceof Array
              ? pathOr('', valueKey, opt)
              : opt[valueKey],
        };
        if (additionalKeys) {
          additionalKeys.forEach((key) => {
            newItem[key] = opt[key];
          });
        }

        return [...prev, newItem];
      }, []);
  if (sorted) {
    opts = sortByProp(opts, 'label');
  }

  return opts;
};

export const lowercaseFirstLetter = (string) => {
  return `${string.charAt(0).toLowerCase()}${string.substr(1, string.length)}`;
};

const getFormColumnName = (formColumnNames, fieldName) => {
  return formColumnNames?.find(
    (formColumnName) => fieldName === formColumnName,
  );
};

const calculateOtherFields = (values) => {
  if (!values?.assetChecklist) {
    return values;
  }
  const updatedValues = { ...values };
  // Set fees to convert cash to 0.00 and set cash value to market value...
  Object.entries(values.assetChecklist).forEach(([propertyName, value]) => {
    // Iterate over lists
    if (propertyName.endsWith('List')) {
      const updatedList = value.map((fieldColumn) => ({
        ...fieldColumn,
        feesToConvertCash: '0.00',
        cashValue: fieldColumn.marketValue,
        annualIncome: (
          (parseFloat(fieldColumn.marketValue) *
            parseFloat(fieldColumn.interestRate ?? 0)) /
          100
        ).toFixed(2),
      }));
      updatedValues.assetChecklist[propertyName] = updatedList;
    } else {
      const columnName = getFormColumnName(
        ALT_ASSET_FORM_FIELD_NAMES,
        propertyName,
      );
      if (columnName && updatedValues.assetChecklist[columnName] === 'yes') {
        const marketValue =
          values.assetChecklist[`${columnName}Asset${ALT_MARKET_VALUE_NAME}`];
        const interestRate =
          values.assetChecklist[`${columnName}Asset${ALT_INTEREST_RATE_NAME}`];
        const annualIncome =
          marketValue && interestRate
            ? (
                (parseFloat(marketValue) * parseFloat(interestRate)) /
                100
              ).toFixed(2)
            : '0.00';

        if (marketValue) {
          updatedValues.assetChecklist[
            `${columnName}Asset${ALT_FEES_TO_CONVERT_CASH_NAME}`
          ] = '0.00';
          updatedValues.assetChecklist[
            `${columnName}Asset${ALT_CASH_VALUE_NAME}`
          ] = marketValue;
          updatedValues.assetChecklist[
            `${columnName}Asset${ALT_ANNUAL_INCOME_NAME}`
          ] = annualIncome;
        }
      }
    }
  });
  return updatedValues;
};

export const calculateDisabledFields = (values) => {
  if (!values?.incomeChecklist) {
    return values;
  }

  const updatedValues = { ...values };
  Object.entries(values.incomeChecklist).forEach(([propertyName, value]) => {
    // Iterate over lists
    if (propertyName.endsWith('List')) {
      const updatedList = value.map((fieldColumn) => {
        const payRate = fieldColumn.PayRate;
        const payFrequency = fieldColumn.PayFrequency;
        const payFreqIsWeeklyMonthlyAnnually = [
          'weekly',
          'monthly',
          'annually',
        ].includes(payFrequency);
        const hoursWeeksMonths = payFreqIsWeeklyMonthlyAnnually
          ? fieldColumn?.HoursWeeksMonths ??
            payFrequencyOptionsMap?.[payFrequency]?.value
          : fieldColumn?.HoursWeeksMonths;
        const payFreqInWeeks = payFreqIsWeeklyMonthlyAnnually
          ? 1
          : payFrequencyOptionsMap?.[payFrequency]?.value ??
            payFrequencyOptionsMap.default.value;
        return {
          ...fieldColumn,
          Income: calculateIncome(
            Number(payFreqInWeeks ?? 0),
            Number(payRate ?? 0),
            Number(hoursWeeksMonths ?? 0),
          ).toFixed(2),
          HoursWeeksMonths: hoursWeeksMonths?.toString?.(),
        };
      });
      updatedValues.incomeChecklist[propertyName] = updatedList;
    } else {
      const columnName = getFormColumnName(
        ALT_INCOME_FORM_FIELD_NAMES,
        propertyName,
      );
      if (columnName && updatedValues.incomeChecklist[columnName] === 'yes') {
        const payRate = values.incomeChecklist[`${columnName}PayRate`];
        const payFrequency =
          values.incomeChecklist[`${columnName}PayFrequency`];

        const payFreqIsWeeklyMonthlyAnnually = [
          'weekly',
          'monthly',
          'annually',
        ].includes(payFrequency);
        const hoursWeeksMonths = payFreqIsWeeklyMonthlyAnnually
          ? values.incomeChecklist?.[`${columnName}HoursWeeksMonths`] ??
            payFrequencyOptionsMap?.[payFrequency]?.value
          : values.incomeChecklist?.[`${columnName}HoursWeeksMonths`];
        const payFreqInWeeks = payFreqIsWeeklyMonthlyAnnually
          ? 1
          : payFrequencyOptionsMap?.[payFrequency]?.value ??
            payFrequencyOptionsMap.default.value;
        const income = calculateIncome(
          Number(payFreqInWeeks),
          Number(payRate),
          Number(hoursWeeksMonths),
        );
        updatedValues.incomeChecklist[`${columnName}Income`] =
          income.toFixed(2);

        if (hoursWeeksMonths) {
          updatedValues.incomeChecklist[`${columnName}HoursWeeksMonths`] =
            hoursWeeksMonths.toString();
        }
      }
    }
  });
  return updatedValues;
};

export const calculateDisabilityFields = (hasDisability, values = {}) => {
  if (hasDisability === 'yes') {
    return {
      ...values,
      hasDisability,
    };
  }

  if (hasDisability === 'no') {
    return {
      hasDisability,
      disabilityHearing: 'no',
      disabilityMobile: 'no',
      disabilityOther: 'no',
      disabilityVisual: 'no',
    };
  }

  return {
    hasDisability,
    disabilityHearing: null,
    disabilityMobile: null,
    disabilityOther: null,
    disabilityVisual: null,
  };
};

export const getFormData = (form, isAltForm, isFormTypeIncome) => {
  const values = {};
  const data = new FormData(form);
  for (const [name, value] of data.entries()) {
    if (value) {
      values[name] = value;
    }
  }
  let formattedObj = dot.object(values);
  if (isAltForm && !isFormTypeIncome) {
    formattedObj = calculateOtherFields(formattedObj);
  }

  if (isAltForm && isFormTypeIncome) {
    formattedObj = calculateDisabledFields(formattedObj);
  }

  return formattedObj;
};

export const handleValidation = async (values, schema, options) => {
  try {
    return await schema.validate(values, {
      abortEarly: false,
      ...options,
    });
  } catch (error) {
    const { inner } = error;
    const errors = {};
    inner.forEach(({ name, path, type, message }) => {
      if (name === 'ValidationError') {
        if (!errors[path]) {
          errors[path] = message;
        }
        if (type === 'required') {
          errors[path] = message;
        }
      }
    });
    throw errors;
  }
};

/**
 * A "Simple Form" is a one-page `<Form />` that has a very predictable flow.
 * Field values are validated against a `schema`, a single submit `<Button />` posts
 * the values and calls `onSubmit()` and the response is either successful or not.
 *
 * This pattern is repeated in quite a few places throughout the app - Login, Register,
 * Lost Password, Claim Account, and (more recently) the Household Forms - and
 * all can use the `useSimpleForm()` hook to handle a lot of the overhead.
 *
 * If a form does not neatly fit this problem (Lease Application, for example),
 * the inner workings of this function can be broken out and modified as needed.
 *
 * This is just a simple wrapper to reduce repetitive boilerplate.
 *
 * @param {Object} schema Form Schema Object, typically built in `yup`
 * @param {Function} onSubmit Called and provided validated `values` as its only prop
 * @param {Function} onSubmitSuccess Called when `onSubmit` is successful
 * @returns {Object}
 *  - handleSubmit, Function, often used directly in the `onSubmit` of the `<Form />`
 *  - status, Object
 *    - isSubmitting, Boolean, whether or not the Form is currently submitting
 *    - formErrors, Object, contains any validation errors from the `schema`
 *    - serverError, String, response from the backend in the event of an error
 *    - responseError, String, unformatted response from the backend in the event of an error
 */
export const useSimpleForm = (
  schema,
  onSubmit,
  onSubmitSuccess,
  isAltForm,
  isFormTypeIncome,
) => {
  const [status, setStatus] = useState({
    isSubmitting: false,
    formData: {},
    formErrors: {},
    serverError: null,
    responseError: null,
  });

  const handleSubmit = async (values) => {
    setStatus({
      ...status,
      isSubmitting: true,
    });

    try {
      const validatedValues = await handleValidation(
        values,
        schema,
        {},
        isAltForm,
        isFormTypeIncome,
      );
      let response = null;
      try {
        response = await onSubmit(validatedValues);
      } catch (error) {
        // apollo graphql request throws error
        response = {
          error: error.message,
          statusCode: error.statusCode ?? 400,
        };
      }
      if (response?.error) {
        setStatus({
          ...status,
          isSubmitting: false,
          formData: dot.dot(values),
          formErrors: {},
          serverError: `[${response.statusCode}] ${response.error}`,
          responseError: response.error,
        });

        return true;
      }

      setStatus({
        ...status,
        formErrors: {},
        serverError: null,
        responseError: null,
        isSubmitting: false,
      });

      return onSubmitSuccess(response);
    } catch (e) {
      setStatus({
        ...status,
        isSubmitting: false,
        formData: dot.dot(values),
        formErrors: e,
        serverError: null,
        responseError: null,
      });

      return true;
    }
  };

  return {
    handleSubmit,
    status,
    setFormData: setStatus,
  };
};

/**
 * Given a bucket of data, produces a config used by the My Application components and hooks.
 *
 * @param {Object} params
 *    @param {string} params.organizationId
 *    @param {string} params.propertyId
 *    @param {string} params.applicationId
 *    @param {string} params.applicantId
 *    @param {boolean} params.isApplicant
 *    @param {boolean} params.isResident
 *    @param {boolean} params.isPrimaryProspect
 *    @param {boolean} params.needsRecert
 *    @param {string} params.householdType
 * @returns {Object}
 */
export const getMyApplicationFormConfig = ({
  organizationId,
  propertyId,
  applicationId,
  applicantId,
  isApplicant,
  isResident,
  isPrimaryProspect,
  recertification,
  needsRecert,
  householdType,
  propertyState,
  propertyCity,
  flags,
}) => {
  const { doNotWishToProvideDisability = false } = flags ?? {};
  const isAffordable = HOUSEHOLD_TYPE_AFFORDABLE === householdType;
  const cityIsSeattle = propertyCity === 'Seattle';

  let needsRecertification = false;
  if (
    recertification.canStart === true ||
    recertification.canContinue === true
  ) {
    needsRecertification = true;
  } else {
    needsRecertification = needsRecert;
  }

  /**
   * Application Endpoints
   */
  const primaryEndpoints = {
    get: `/${organizationId}/${propertyId}/primaryForms/${applicantId}`,
    submit: {
      action: 'post',
      url: `/${organizationId}/${propertyId}/primaryForms/${applicantId}`,
    },
  };

  const secondaryEndpoints = {
    get: `/${organizationId}/${propertyId}/secondaryForms/${applicantId}`,
    submit: {
      action: 'post',
      url: `/${organizationId}/${propertyId}/secondaryForms/${applicantId}`,
    },
  };

  const recertEndpoints = {
    get: `/${organizationId}/${propertyId}/applicant/${applicantId}/recert-form`,
    submit: {
      action: 'put',
      url: `/${organizationId}/${propertyId}/${applicationId}/${applicantId}/recert-form`,
    },
  };

  /**
   * Conventional Config
   */
  if (isApplicant === true && isAffordable === false) {
    return {
      workflow: cityIsSeattle
        ? APPLICATION_CONVENTIONAL_WA
        : APPLICATION_CONVENTIONAL,
      endpoints:
        isPrimaryProspect === true ? primaryEndpoints : secondaryEndpoints,
    };
  }

  /**
   * Affordable Config
   */
  if (isApplicant && isAffordable) {
    const nonWaPrimaryAffordableForm = doNotWishToProvideDisability
      ? APPLICATION_PRIMARY_AFFORDABLE_WITH_GLOBAL_DISABILITY
      : APPLICATION_PRIMARY_AFFORDABLE;
    const nonWaSecondaryAffordableForm = doNotWishToProvideDisability
      ? APPLICATION_SECONDARY_AFFORDABLE_WITH_GLOBAL_DISABILITY
      : APPLICATION_SECONDARY_AFFORDABLE;
    if (isPrimaryProspect) {
      return {
        workflow: cityIsSeattle
          ? APPLICATION_PRIMARY_AFFORDABLE_WA
          : nonWaPrimaryAffordableForm,
        endpoints: primaryEndpoints,
      };
    }

    return {
      workflow: cityIsSeattle
        ? APPLICATION_SECONDARY_AFFORDABLE_WA
        : nonWaSecondaryAffordableForm,
      endpoints: secondaryEndpoints,
    };
  }

  /**
   * Recert Config
   */
  if (
    isResident === true &&
    isAffordable === true &&
    needsRecertification === true
  ) {
    if (isPrimaryProspect === true) {
      return {
        workflow: APPLICATION_PRIMARY_RECERT,
        endpoints: recertEndpoints,
      };
    }

    return {
      workflow: APPLICATION_SECONDARY_RECERT,
      endpoints: recertEndpoints,
    };
  }

  return false;
};

/**
 * Gets the endpoint used for the GETting a property's terms and conditions
 * @param {string} organizationId
 * @param {string} propertyId
 */
export const getTermsAndConditions = (organizationId, propertyId) =>
  `/${organizationId}/${propertyId}/property-terms-and-conditions`;
